/** Absolute http(s) URLs — common when originalName is missing and we fall back to sourceUrl. */
const ABSOLUTE_URL = /^https?:\/\//i;

/**
 * Storage keys that are mostly a content hash: 32+ hex chars, optional short
 * segment suffix (`_0-10`), then an optional file extension.
 */
const HASH_STORAGE_KEY = /^[a-f0-9]{32,}(_[a-z0-9-]+)?(\.[a-z0-9]+)?$/i;

const MIDDLE_TRUNCATE_MIN_LENGTH = 48;
const MIDDLE_HEAD_CHARS = 8;
const MIDDLE_TAIL_CHARS = 12;

/**
 * Prefer a readable filename over a raw CDN URL. Leaves non-URL strings unchanged.
 */
export function assetBasename(name: string): string {
  if (!ABSOLUTE_URL.test(name)) return name;

  try {
    const { pathname } = new URL(name);
    const segment = pathname.split("/").filter(Boolean).at(-1);
    if (!segment) return name;
    return decodeURIComponent(segment);
  } catch {
    return name;
  }
}

/**
 * Shortens hash-like storage filenames for display (`abc12345…7890.mp4`) and
 * strips CDN URLs down to their basename first. Human-readable names are left
 * unchanged for end truncation in the UI.
 */
export function formatAssetDisplayName(name: string): string {
  const baseName = assetBasename(name);

  if (
    baseName.length < MIDDLE_TRUNCATE_MIN_LENGTH ||
    !HASH_STORAGE_KEY.test(baseName)
  ) {
    return baseName;
  }

  const extMatch = baseName.match(/(\.[a-z0-9]+)$/i);
  const ext = extMatch?.[1] ?? "";
  const stem = ext ? baseName.slice(0, -ext.length) : baseName;
  const tailChars = Math.min(MIDDLE_TAIL_CHARS, stem.length);
  const tail = stem.slice(-tailChars) + ext;

  if (baseName.length <= MIDDLE_HEAD_CHARS + 1 + tail.length) {
    return baseName;
  }

  return `${stem.slice(0, MIDDLE_HEAD_CHARS)}…${tail}`;
}

/**
 * Creatives ingested by URL carry no filename, and their CDN link is an opaque token that
 * identifies nothing to a viewer — number them by their position in the report instead and
 * leave the link to the tooltip and the open-source affordance.
 */
export function creativeDisplayLabel(
  originalName: string | null,
  position: number,
  fallbackLabel: string,
): string {
  return originalName ?? `${fallbackLabel} ${position}`;
}
